/**
 * 보고서를 PDF 로 내려받는다
 * ---------------------------------------------------------------------------
 * ## 왜 화면을 그대로 찍나
 * 보고서는 이미 문서 모양으로 그려져 있다(ReportDocument + ReportCharts).
 * PDF 를 따로 조판하면 **두 벌을 관리하게 되고**, 한쪽만 고쳐져 화면과
 * 인쇄물이 달라진다. 보이는 것을 그대로 찍는다.
 *
 * ## 화면 밖에 사본을 만들어 찍는다
 * 보고서는 스크롤 상자 안에 있다. 그 상자를 그대로 찍으면 **보이는 만큼만**
 * 찍히고 아래는 잘린다. 그래서 잘라 낼 수 없는 사본을 화면 밖에 펼쳐 놓고
 * 찍은 뒤 지운다.
 *
 * ## 라이브러리를 열 때 받아 온다
 * html2pdf 는 무겁다(jsPDF + html2canvas). 보고서를 보기만 하는 사람에게
 * 처음 화면에서 같이 내려보낼 이유가 없어 누를 때 가져온다.
 */

/** A4 폭에서 여백을 뺀 값(px 환산). 캡처할 사본의 너비로 쓴다. */
const PAGE_WIDTH_PX = 780;

export async function downloadReportPdf(options: {
  /** 찍을 원본. 이 요소의 내용을 사본으로 떠서 찍는다 */
  source: HTMLElement;
  /** 파일 이름(확장자 제외) */
  fileName: string;
}): Promise<void> {
  const { source, fileName } = options;

  /*
   * 화면 밖 사본.
   *
   * `display:none` 은 쓸 수 없다 — 크기가 0 이 되어 차트(SVG)가 빈 그림으로
   * 찍힌다. 보이지만 화면 밖(왼쪽 -10000px)에 두어야 레이아웃이 계산된다.
   */
  const clone = source.cloneNode(true) as HTMLElement;
  const holder = document.createElement('div');
  holder.style.position = 'fixed';
  holder.style.left = '-10000px';
  holder.style.top = '0';
  holder.style.width = `${PAGE_WIDTH_PX}px`;
  holder.style.background = '#ffffff';
  holder.style.padding = '24px';
  holder.style.zIndex = '-1';

  /* 스크롤 상자를 펼친다 — 사본에서는 잘릴 이유가 없다. */
  clone.style.maxHeight = 'none';
  clone.style.overflow = 'visible';
  clone.style.height = 'auto';
  clone.querySelectorAll<HTMLElement>('*').forEach((el) => {
    if (el.style.maxHeight) el.style.maxHeight = 'none';
    if (el.style.overflowY) el.style.overflowY = 'visible';
    if (el.style.overflowX) el.style.overflowX = 'visible';
  });

  holder.appendChild(clone);
  document.body.appendChild(holder);

  try {
    const mod = await import('html2pdf.js');
    const html2pdf = (mod as { default?: unknown }).default ?? mod;

    await (html2pdf as (...a: unknown[]) => { set: (o: unknown) => { from: (e: unknown) => { save: () => Promise<void> } } })()
      .set({
        margin: [10, 10, 12, 10],
        filename: `${fileName}.pdf`,
        image: { type: 'jpeg', quality: 0.96 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          /* 사본은 화면 밖에 있다. 스크롤 보정을 끄지 않으면 빈 곳이 찍힌다 */
          scrollX: 0,
          scrollY: 0,
          windowWidth: PAGE_WIDTH_PX + 48,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        /* 표와 차트가 페이지 경계에서 잘리지 않게 */
        pagebreak: { mode: ['css', 'avoid-all'] },
      })
      .from(clone)
      .save();
  } finally {
    document.body.removeChild(holder);
  }
}

/** `(주)컴퍼니 본사` 처럼 파일 이름에 못 쓰는 글자를 걷어낸다. */
export function safeFileName(text: string): string {
  return text.replace(/[\\/:*?"<>|]/g, '_').trim() || '보고서';
}
